import React from "react";
import { toast } from "sonner";
import { Copy } from "@phosphor-icons/react";
import { H } from "./parts";

const SCORE_LABELS = {
  pricing: "Price vs past claims", scope: "Work claimed", evidence: "Evidence (repair order, photos)",
  eligibility: "Warranty eligibility", extraction: "Reading the repair order",
};

export const band = (v, t = {}) => (v >= (t.confidence_auto_approve ?? 90) ? "#73bf44" : v >= (t.confidence_further_info ?? 61) ? "#d9b45b" : "#ef4444");

export function ConfidenceRing({ value, thresholds, size = 96 }) {
  const r = size / 2 - 7;
  const c = 2 * Math.PI * r;
  const color = band(value, thresholds);
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid="wi-ai-confidence-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#262626" strokeWidth="7" fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth="7" fill="none" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - value / 100)} style={{ transition: "stroke-dashoffset 700ms ease" }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-heading font-bold text-white tabular-nums" style={{ fontSize: size * 0.27 }}
        data-testid="wi-ai-confidence-value">{value}%</div>
    </div>
  );
}

export function ScoreBars({ scores, weights }) {
  return (
    <div className="space-y-2" data-testid="wi-ai-score-bars">
      {Object.keys(SCORE_LABELS).filter((k) => scores?.[k] != null).map((k) => (
        <div key={k} data-testid={`wi-ai-score-${k}`}>
          <div className="flex justify-between text-[0.7rem] mb-0.5"><span className="text-neutral-400">{SCORE_LABELS[k]} <span className="text-neutral-600">· {weights?.[k]}% of the score</span></span>
            <span className="tabular-nums text-neutral-200">{scores[k]}%</span></div>
          <div className="h-1.5 bg-neutral-800 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${scores[k]}%`, background: band(scores[k]) }} /></div>
        </div>
      ))}
    </div>
  );
}

export function Concerns({ a }) {
  const items = [...(a.blockers || []).map((b) => (b.detail ? `${b.label} (${b.detail})` : b.label)), ...(a.ai?.missing_information || []).map((m) => `Missing: ${m}`)];
  if (!items.length) return null;
  return (
    <div data-testid="wi-ai-concerns"><H>Concerns</H>
      <ul className="text-xs text-amber-200/90 space-y-1 list-disc pl-4">{items.map((x) => <li key={x}>{x}</li>)}</ul>
    </div>
  );
}

export function DealerQuestions({ items }) {
  if (!items?.length) return null;
  const copy = () => navigator.clipboard.writeText(items.map((q, i) => `${i + 1}. ${q}`).join("\n")).then(() => toast.success("Questions copied")).catch(() => toast.error("Couldn't copy"));
  return (
    <div data-testid="wi-ai-questions">
      <div className="flex items-center justify-between"><H>Questions to ask the dealer</H>
        <button onClick={copy} className="text-[0.7rem] text-neutral-500 hover:text-white flex items-center gap-1 mb-2" data-testid="wi-ai-questions-copy"><Copy size={12} /> Copy</button></div>
      <ol className="text-xs text-neutral-300 space-y-1 list-decimal pl-4">{items.map((q) => <li key={q}>{q}</li>)}</ol>
    </div>
  );
}
